import { useTranslation } from 'react-i18next';
import type { QcRunResponse } from '../api.ts';
import { StatusBanner } from './StatusBanner.tsx';
import { CheckRow } from './CheckRow.tsx';
import { History } from './History.tsx';
import { ResultActions, type NotePostedMeta } from './ResultActions.tsx';
import styles from './ResultsList.module.css';

export type { NotePostedMeta };

/** Maps overall status to the status-word CSS modifier (colors live in the stylesheet). */
const overallStatusClass: Record<string, string> = {
  PASS: styles.overallPass,
  WARN: styles.overallWarn,
  FAIL: styles.overallFail,
  ERROR: styles.overallError,
};

interface ResultsListProps {
  run: QcRunResponse;
  /** True only for the latest run — Submit is enabled; false = viewing a historical run. */
  allowPostNote?: boolean;
  /** All runs for this contact + suite, shown in the header-row dropdown. */
  history?: QcRunResponse[];
  /** View a previous run (refetches the contact card; does not re-run checks). */
  onSelectRun?: (run: QcRunResponse) => void;
  /** Called after a successful post so the parent can flip the run to posted. */
  onPosted?: (meta: NotePostedMeta) => void;
  /** Re-run checks on the current contact (ResultActions "Run Again"). */
  onRerun?: () => void;
  /** Clear and return to the empty input (ResultActions "Start Over"). */
  onStartOver?: () => void;
}

export function ResultsList({
  run,
  allowPostNote = false,
  history = [],
  onSelectRun,
  onPosted,
  onRerun,
  onStartOver,
}: ResultsListProps) {
  const { t } = useTranslation('ui');
  let lastGroup: string | null | undefined = null;

  return (
    <div className={styles.container}>
      {!allowPostNote && (
        <div className={styles.historicalBanner}>{t('submit.historicalBanner')}</div>
      )}
      <StatusBanner
        contactId={run.contactId}
        contactSummary={run.contactSummary}
      />
      <div className={styles.checks}>
        <div className={styles.checksHeader}>
          <span className={styles.overallStatus}>
            <span className={styles.overallLabel}>{t('overall.label')}</span>
            <span className={`${styles.overallStatusWord} ${overallStatusClass[run.overallStatus] ?? ''}`}>
              {t(`overall.${run.overallStatus}`)}
            </span>
          </span>
          {onSelectRun && (
            <History current={run} runs={history} onSelect={onSelectRun} />
          )}
        </div>
        {run.results.map((r) => {
          const showGroup = r.group && r.group !== lastGroup;
          lastGroup = r.group ?? null;
          return (
            <div key={r.checkId}>
              {showGroup && (
                <div className={styles.groupHeading}>{r.group}</div>
              )}
              <CheckRow result={r} />
            </div>
          );
        })}

        {(onRerun || onStartOver || onPosted) && (
          <ResultActions
            key={run.runId}
            run={run}
            allowSubmit={allowPostNote}
            onSubmitted={(meta) => onPosted?.(meta)}
            onRerun={() => onRerun?.()}
            onStartOver={() => onStartOver?.()}
          />
        )}
      </div>
    </div>
  );
}
